<template>
  <v-stepper v-bind="stepperProps" v-model="currentStep" :class="stepperClasses.join(' ')">
    <v-stepper-header :class="[stepperHeaderClasses.join(' '), { 'no-print': isCardInfoPrint }]">
      <v-slide-group :value="currentStep - 1" center-active :show-arrows="alwaysShowArrows ? 'desktop' : true">
        <template v-for="(step, idx) of steps">
          <v-slide-item :key="idx">
            <v-stepper-step
              :step="step.step"
              class="px-0"
              :complete="currentStep > step.step"
              :editable="isEditable(step)"
              edit-icon="mdi-check"
            >
              <template v-if="step.straightTitle || step.translationPath">
                <v-row no-gutters class="stepper-header-title">
                  {{ step.straightTitle || $i18n.t(step.translationPath) }}
                </v-row>
                <v-row
                  no-gutters
                  class="stepper-header-subtitle"
                  justify="center"
                  v-if="step.straightSubtitle || step.subtitlePath"
                >
                  {{ step.straightSubtitle || $i18n.t(step.subtitlePath) }}
                </v-row>
              </template>
            </v-stepper-step>
          </v-slide-item>
        </template>
      </v-slide-group>
    </v-stepper-header>
    <v-stepper-items :class="stepperItemsClasses.join(' ')">
      <v-stepper-content v-for="(step, idx) of steps" :key="idx" :step="step.step" class="pa-0">
        <slot :name="step.step" v-bind="step">
          {{ step }}
        </slot>
      </v-stepper-content>
    </v-stepper-items>
    <v-row
      no-gutters
      class="stepper-buttons-row"
      :class="[stepperButtonsClasses.join(' '), { 'no-print': isCardInfoPrint }]"
      v-if="!hideButtons"
    >
      <v-col cols="auto">
        <v-btn
          text
          color="primary"
          class="stepper-button px-0"
          @click="back"
          :disabled="!isBackButtonEnabled"
          :loading="isBackButtonLoading"
        >
          <v-icon v-text="'mdi-chevron-left'" />
          {{ $i18n.t('default.previous') }}
        </v-btn>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn
          text
          color="primary"
          class="stepper-button px-0"
          @click="next"
          :disabled="!isNextButtonEnabled"
          :loading="isNextButtonLoading"
        >
          {{ $i18n.t('default.next') }}
          <v-icon v-text="'mdi-chevron-right'" />
        </v-btn>
      </v-col>
    </v-row>
  </v-stepper>
</template>
<script>
import { get } from 'vuex-pathify';

export default {
  name: 'Stepper',
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
    alwaysShowArrows: {
      type: Boolean,
      default: false,
    },
    steps: {
      type: Array,
      default: () => {
        return [];
      },
    },
    stepperClasses: {
      type: Array,
      default: () => {
        return [];
      },
    },
    stepperButtonsClasses: {
      type: Array,
      default: () => {
        return [];
      },
    },
    stepperHeaderClasses: {
      type: Array,
      default: () => {
        return [];
      },
    },
    stepperItemsClasses: {
      type: Array,
      default: () => {
        return [];
      },
    },
    stepperProps: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    isCardInfoPrint: get('tools/toolStatus@isCardInfoPrintActive'),
    currentStepProps() {
      return this.steps.find(step => step.step === this.currentStep);
    },
    isBackButtonEnabled() {
      if (this.currentStepProps.backDisabled) {
        return false;
      } else if (this.currentStep > 1) {
        return true;
      } else {
        return false;
      }
    },
    isBackButtonLoading() {
      if (this.currentStepProps.backLoading) {
        return true;
      } else {
        return false;
      }
    },
    isNextButtonEnabled() {
      if (this.currentStepProps.nextDisabled) {
        return false;
      } else if (this.currentStep < this.steps.length) {
        return true;
      } else {
        return false;
      }
    },
    isNextButtonLoading() {
      if (this.currentStepProps.nextLoading) {
        return true;
      } else {
        return false;
      }
    },
  },
  data: () => ({
    currentStep: 1,
  }),
  methods: {
    back() {
      this.currentStep -= 1;
    },
    isEditable(stepProps) {
      const { disabled = false, step } = stepProps;
      if (!this.editable || disabled) {
        return false;
      } else {
        if (this.currentStep > step) {
          if (this.isBackButtonEnabled && !this.isBackButtonLoading) {
            return true;
          }
        } else if (this.currentStep < step) {
          if (this.isNextButtonEnabled && !this.isNextButtonLoading) {
            return true;
          }
        }
        return false;
      }
    },
    next() {
      this.currentStep += 1;
    },
    reset() {
      this.setStep(this.steps[0].step);
    },
    setStep(step) {
      this.currentStep = step;
    },
  },
  mounted() {
    this.currentStep = this.steps[0].step;
  },
  watch: {
    currentStep(nV, oV) {
      this.$emit('stepChange', { nV, oV });
    },
  },
};
</script>
<style lang="scss" scoped>
.stepper-header-title {
  justify-content: center;
  text-align: center !important;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
  margin: auto;
}
.stepper-header-subtitle {
  text-align: center !important;
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 10px;
  line-height: 12px;
  margin: auto;
}

::v-deep {
  .v-stepper__step {
    flex-basis: 90px !important;
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  .v-stepper__step__step::before {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    background-color: white;
    border-radius: 100%;
    z-index: -10;
  }
  .v-slide-group__next,
  .v-slide-group__prev {
    min-width: 20px !important;
  }
  .v-slide-group__content {
    white-space: initial !important;
    flex: 1 0 0 !important;
    justify-content: space-between;
  }
  .v-slide-group__content::before {
    content: '';
    height: 1px;
    width: 100%;
    position: absolute;
    top: 35px;
    z-index: -99;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
  .v-slide-group {
    width: 100%;
    z-index: 0;
  }
  .v-stepper__step:first-of-type::before {
    content: '';
    height: 5px;
    width: calc(50% - 12px);
    position: absolute;
    top: 35px;
    z-index: -99;
    right: calc(50% + 12px);
    background-color: white;
  }
  .v-stepper__step:last-of-type::before {
    content: '';
    height: 5px;
    width: calc(50% - 12px);
    position: absolute;
    top: 35px;
    z-index: -99;
    left: calc(50% + 12px);
    background-color: white;
  }
  .v-slide-group__prev--disabled,
  .v-slide-group__next--disabled {
    opacity: 0.3 !important;
  }
}

.v-stepper--alt-labels {
  .v-stepper__header {
    .v-stepper__step {
      flex-basis: 125px !important;
    }
    .v-divider {
      margin: 35px -45px 0 !important;
    }
  }
}
.stepper-buttons-row {
  color: 'blue';
}
.stepper-button {
  font-size: 14px;
  text-transform: initial;
}
</style>
